import { Container, Typography } from '@mui/material'

import { CardPrice } from '@/components/ProductPrice/CardPrice'
import { CartTotalPrice } from '@/components/ProductPrice/CartTotalPrice'
import { ProductDetailsPrice } from '@/components/ProductPrice/ProductDetailsPrice'
import { ProductListPrice } from '@/components/ProductPrice/ProductListPrice'
import { PriceProductList } from '@/components/ProductPrice/ProductPrice.styled'
import { useProductPrice } from '@/hooks/bff/prices/getPrices/useProductPrice'
import { useInViewport } from '@/hooks/useInViewport'

import type { FC } from 'react'
import type { SxProps } from '@mui/material'
import type { ProductProps } from '@/interfaces/product.interface'

export const ProductPrice: FC<{
  /** Product for which the price will be calculated */
  product?: ProductProps
  /** Display a smaller view for Product Cards */
  /** Styles to apply, especially useful for components positioning */
  sx?: SxProps
  priceCard?: boolean
  priceProductDetails?: boolean
  priceList?: boolean
  priceTotalCart?: boolean
  quantity?: number
  'data-testid'?: string
}> = ({
  product,
  sx = {},
  priceCard = false,
  priceProductDetails = false,
  priceList = false,
  priceTotalCart = false,
  quantity = 1,
  'data-testid': testId,
}) => {
  // Lazy-load: only fetch this product's price once its row nears the viewport. On a list of 500
  // products the on-screen rows fetch immediately (batched into a few requests) and the rest fetch
  // as the user scrolls, instead of fetching — or re-rendering — all 500 at once.
  const [viewportRef, inView] = useInViewport<HTMLDivElement>()
  const { priceData: price, isLoading } = useProductPrice(product?.code, quantity, { enabled: inView })

  return (
    <Container
      ref={viewportRef}
      className="price"
      data-testid={testId}
      disableGutters
      sx={{
        alignItems: 'baseline',
        color: 'text.primary',
        display: 'flex',
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {priceTotalCart ? (
        <CartTotalPrice price={price} isLoading={isLoading} />
      ) : priceProductDetails ? (
        <ProductDetailsPrice product={product} price={price} isLoading={isLoading} />
      ) : priceList ? (
        <ProductListPrice price={price} isLoading={isLoading} />
      ) : priceCard ? (
        <CardPrice price={price} isLoading={isLoading} />
      ) : price == null ? (
        <PriceProductList />
      ) : (
        <Typography className="price formattedValue" component="span" variant="h5">
          {price?.formattedValue}
        </Typography>
      )}
    </Container>
  )
}
